import DOMPurify from 'dompurify';
import {Box, Clickable, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';
import {cn, cva} from 'platform/shared';

import {ComponentPropsWithRef, MouseEvent} from 'react';

import {isEmpty, isNil, not, omit} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {getRandomId, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {Button} from '../../Button/Button';
import {Flag} from '../../Flag/Flag';
import {NotificationItemData} from '../NotificationItemData';
import {NotificationAvatar} from './NotificationAvatar';

type NotificationItemProps = Omit<NotificationItemData, 'onClick'> &
  TestIdProps & {
    onNavigate: (url: string) => void;
    onMarkAsRead?: (id: string) => void;
    onClick?: () => Promise<void> | void;
  };

const notificationBackgroundVariants = cva(
  'h-full rounded-sm hover:bg-[var(--color-palettes-neutral-10-100)]',
  {
    variants: {
      isRead: {
        true: 'bg-[var(--color-general-white)]',
        false:
          'bg-[var(--color-palettes-blue-10-100)] hover:bg-[var(--color-palettes-neutral-10-100)]',
      },
    },
    defaultVariants: {
      isRead: true,
    },
  }
);

const messageTextVariants = cva(
  'overflow-hidden text-[length:var(--text-size-x-small)] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]'
);

const unreadIndicatorVariants = cva(
  'size-2 shrink-0 grow-0 rounded-full bg-[var(--color-general-accent)]',
  {
    variants: {
      isVisible: {
        true: 'block',
        false: 'hidden',
      },
    },
  }
);

interface LayoutNotificationBackgroudProps extends ComponentPropsWithRef<'div'> {
  $isRead?: boolean;
}

// tw-migration: the old export was styled.div<{$isRead?: boolean}> (the Backgroud typo is kept
// byte-faithful for API parity); same background/hover styles via notificationBackgroundVariants
export function LayoutNotificationBackgroud(props: LayoutNotificationBackgroudProps) {
  const htmlProps = omit(['$isRead'], props);

  return (
    <div
      {...htmlProps}
      className={cn(
        notificationBackgroundVariants({isRead: props.$isRead !== false}),
        props.className
      )}
    />
  );
}

export function NotificationItem(props: NotificationItemProps) {
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const markAsRead = () => {
    not(props.isRead) && props.onMarkAsRead?.(props.id);
  };

  const onClick = async (event: MouseEvent<HTMLDivElement>) => {
    markAsRead();

    // Cmd/Ctrl+click opens the target in a new tab, matching standard link behaviour. The current
    // view and the notification panel have to stay untouched, so nothing else runs.
    if (isNotNil(props.url) && isOpenInNewTabClick(event)) {
      openInNewTab(props.url);
      return;
    }

    props.url && props.onNavigate(props.url);
    if (isNotNil(props.onClick)) {
      startLoading();
      await props.onClick();
      stopLoading();
    }
  };

  const onMouseWheelClick = () => {
    if (isNil(props.url)) {
      return;
    }
    markAsRead();
    openInNewTab(props.url);
  };

  const isMarkAsReadEnabled = isNotNil(props.onMarkAsRead) && !!props.isRead;
  const hasFlags = isNotNil(props.flags) && not(isEmpty(props.flags));
  const hasActions = isNotNil(props.actions) && not(isEmpty(props.actions));
  const areActionsAboveCreated = props.actionsPosition === 'aboveCreated';

  const actionButtons = props.actions?.map((action) => (
    <Button key={`${action.title}-${getRandomId()}`} {...action} />
  ));

  return (
    <Clickable
      height="100%"
      isDisabled={isNil(props.resource) && not(isMarkAsReadEnabled)}
      onClick={onClick}
      onMouseWheelClick={onMouseWheelClick}
      data-testid={props['data-testid']}
    >
      <div className={notificationBackgroundVariants({isRead: props.isRead !== false})}>
        <Box minHeight={18} padding={2} overflow="hidden">
          <HStack spacing={2} justify="stretch">
            <NotificationAvatar {...props} isPending={isLoading} />
            <VStack spacing={2} grow={1}>
              <VStack spacing={1}>
                <Show when={hasFlags}>
                  <HStack spacing={2} align="baseline" justify="space-between">
                    <HStack spacing={1}>
                      {props.flags?.map((flag) => (
                        <Flag key={`${flag.label}-${getRandomId()}`} size="small" {...flag} />
                      ))}
                    </HStack>
                    <div
                      className={unreadIndicatorVariants({isVisible: not(props.isRead)})}
                      data-testid={suffixTestId('unreadIndicator', props)}
                    />
                  </HStack>
                </Show>

                <HStack spacing={2} align="baseline" justify="space-between">
                  <Text size="xSmall" color="secondary" inline>
                    {props.title}
                  </Text>
                  <div
                    className={unreadIndicatorVariants({
                      isVisible: not(hasFlags) && not(props.isRead),
                    })}
                    data-testid={suffixTestId('unreadIndicator', props)}
                  />
                </HStack>
                <Show when={isNotNilOrEmpty(props.resource)}>
                  <Text size="xSmall" color="link">
                    {props.resource}
                  </Text>
                </Show>
                <Show when={hasActions && areActionsAboveCreated}>
                  <HStack spacing={2} wrap>
                    {actionButtons}
                  </HStack>
                </Show>
                <Show when={props.created}>
                  {(created) => (
                    <Text size="xSmall" color="secondary">
                      <DateTimeFormat format="dateTimeMedium" value={created} />
                    </Text>
                  )}
                </Show>
                <Show when={isNotNilOrEmpty(props.message)}>
                  <Box
                    border="1px solid"
                    borderColor="general.separator"
                    borderRadius="small"
                    padding={2}
                  >
                    <div
                      className={messageTextVariants()}
                      // eslint-disable-next-line eslint-js/no-restricted-syntax, react/no-danger -- Data is sanitized so it's safe to use dangerouslySetInnerHTML
                      dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(props.message ?? '')}}
                    />
                  </Box>
                </Show>
              </VStack>
              <Show when={hasActions && not(areActionsAboveCreated)}>
                <HStack spacing={2}>{actionButtons}</HStack>
              </Show>
            </VStack>
          </HStack>
        </Box>
      </div>
    </Clickable>
  );
}

const isOpenInNewTabClick = (event: MouseEvent<HTMLDivElement>) => event.metaKey || event.ctrlKey;

const openInNewTab = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');
